import { Container, Typography } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';

import Auth from '../../../../api/auth';
import Provision from '../../../../api/provision';
import Accordion from '../../../../components/Accordion/Accordion';
import SuccessModal from '../../../../components/Modal/SuccessModal';
import useApiQuery from '../../../../hooks/useApiQuery';

import EvaluationForm from './EvaluationForm';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg } from '@/types';
import type { VirtualCloud } from '@/types/cloudTypes';
import type { Product } from '@/types/productTypes';

const trialQuota = {
  compute: { cores: 10, ram: 1024 },
  network: { floatingIps: 1 },
  volume: { gigabytes: 10, objectsMegabytes: 0 },
};

const Description = (): React.JSX.Element => (
  <>
    <Typography gutterBottom>
      The evaluation product enables user to place workloads on the pan-net.cloud using Cloud APIs
      and a full set of user documentation. The product includes 10 vCPUs, 10GB RAM and 10GB
      storage. The product is available for a 3 month testing period starting from order
      confirmation. An organization can order 1 evaluation products in total within a time frame of
      one year starting from the creation of the organization in the pan-net.cloud portal.
    </Typography>
    <Typography gutterBottom>
      This product is governed by the General Terms & Conditions (GT&C) for using pan-net.cloud.
    </Typography>
    <Typography gutterBottom>
      The Product Specification for evaluation products is applicable as published on the
      pan-net.cloud portal.
    </Typography>
    <Typography gutterBottom>
      The Service Level Agreement is not applicable for evaluation products.
    </Typography>
  </>
);

interface EvaluationFormValues {
  osProjectName: string;
}

const Evaluation = (): React.JSX.Element => {
  const navigate = useNavigate();
  const userData = useUserData();

  const [trialProduct, setTrialProduct] = useState<Product | null>(null);
  const [virtualCloud, setVirtualCloud] = useState<VirtualCloud>({
    id: '',
    projects: [],
    allowed_quota: {
      compute: { cores: 0, ram: 0 },
      volume: {
        gigabytes: 0,
        objectsMegabytes: 0,
        objects_megabytes: 0,
      },
      network: {
        floatingIps: 0,
        floating_ips: 0,
      },
    },
    used_quota: {
      compute: { cores: 0, ram: 0 },
      volume: {
        gigabytes: 0,
        objectsMegabytes: 0,
        objects_megabytes: 0,
      },
      network: {
        floatingIps: 0,
        floating_ips: 0,
      },
    },
    unusedQuota: {
      compute: { cores: 0, ram: 0 },
      volume: { gigabytes: 0, objectsMegabytes: 0 },
      network: { floatingIps: 0 },
    },
    cloud: { name: '' },
  });
  const [osProjectOrdered, setOsProjectOrdered] = useState<boolean>(false);

  const { makeApiCall: fetchProducts } = useApiQuery(Auth.fetchV2Products as unknown as APIHookArg);
  const { makeApiCall: getVirtualCloudsCall } = useApiQuery(
    Provision.fetchVirtualClouds as unknown as APIHookArg
  );
  const { makeApiCall: createOsProject } = useApiQuery(
    Provision.createOsProject as unknown as APIHookArg
  );

  const trialYearRevoked = useMemo(() => {
    const oneYearMs = 365 * 24 * 3600 * 1000;
    return userData.organization?.createdAt
      ? new Date().getTime() - new Date(userData.organization.createdAt).getTime() > oneYearMs
      : false;
  }, [userData.organization?.createdAt]);

  const trialEnabled = useMemo(
    () => userData.organization?.marketplaceItems?.some(item => item.name === 'Evaluation'),
    [userData.organization?.marketplaceItems]
  );

  useEffect(() => {
    if (trialYearRevoked || !trialEnabled) return;

    const getTrialProduct = async (): Promise<void> => {
      const response = await fetchProducts({ types: ['evaluation'] });
      const data = (
        response && typeof response === 'object' && 'docs' in response
          ? (response as { docs: Product[] }).docs
          : response
      ) as Product[];
      if (data.length && data[0]) {
        setTrialProduct(data[0]);
      }
    };

    getTrialProduct();
  }, [trialYearRevoked, trialEnabled, fetchProducts]);

  useEffect(() => {
    if (!trialProduct) return;

    const getTrialVirtualCloud = async (): Promise<void> => {
      const { juiceGroup } = trialProduct.details;
      const data = (await getVirtualCloudsCall({ juiceGroups: [juiceGroup] })) as {
        cloud: { name: string };
        projects: unknown[];
      }[];

      if (data.length > 0) {
        setVirtualCloud(
          prev =>
            ({
              ...prev,
              id: data[0]?.cloud.name ?? '',
              projects: data[0]?.projects,
            }) as VirtualCloud
        );
      }
    };

    getTrialVirtualCloud();
  }, [trialProduct, getVirtualCloudsCall]);

  const handleSubmit = async (values: EvaluationFormValues): Promise<void> => {
    const data = await createOsProject({
      osProjectName: values.osProjectName,
      cloudId: virtualCloud.id,
      type: 'computeTrial',
      juiceGroup: trialProduct?.details.juiceGroup,
      projectId: trialProduct?._id,
      productId: trialProduct?._id,
      organizationId: userData.organizationId,
      requestedQuota: trialQuota,
    });

    if (data) setOsProjectOrdered(true);
  };

  const renderEvaluationDetails = (): React.JSX.Element => {
    if (trialYearRevoked) {
      return <Typography>Your right to use the free trial product has expired.</Typography>;
    }
    if (!trialEnabled) {
      return <Typography>Trial product is not enabled for your organization.</Typography>;
    }
    if (!trialProduct) {
      return <Typography>You are not assigned to trial product.</Typography>;
    }

    return virtualCloud.projects?.length ? (
      <>
        <Typography display='inline'>
          Your compute package is allocated to the trial product of your organization,
        </Typography>
        <Typography
          variant='h6'
          display='inline'
        >
          {' '}
          {trialProduct.name}
        </Typography>
        <Typography gutterBottom>
          Navigate to the products page by clicking{' '}
          <Link to='/products/compute'>
            <Typography
              component='span'
              display='inline'
            >
              here
            </Typography>
          </Link>
        </Typography>
      </>
    ) : (
      <>
        <Typography display='inline'>
          Your compute package will be allocated to the trial product of your organization,
        </Typography>
        <Typography
          variant='h6'
          display='inline'
        >
          {' '}
          {trialProduct.name}
        </Typography>
      </>
    );
  };

  return (
    <Container maxWidth='md'>
      {osProjectOrdered && (
        <SuccessModal
          title='Evaluation OS-Project Ordered'
          messageId='createOsProjectFulfilled'
          onSubmit={() => {
            navigate('/products/compute');
          }}
          onClose={() => {
            navigate('/products/compute');
          }}
        />
      )}
      <Typography
        variant='h4'
        color='primary'
        display='inline'
      >
        Evaluation product
      </Typography>
      <Typography
        variant='h5'
        display='inline'
      >
        {' '}
        (Free of charge)
      </Typography>
      <Accordion
        expanded
        title='Project Description'
        details={<Description />}
      />
      <br />
      {renderEvaluationDetails()}
      <Typography>Define a name for the OpenStack project (tenant):</Typography>
      <EvaluationForm
        disabled={
          !trialProduct ||
          trialYearRevoked ||
          !trialEnabled ||
          (virtualCloud.projects?.length ?? 0) > 0
        }
        onSubmit={handleSubmit}
      />
    </Container>
  );
};

export default Evaluation;
